<template>
  <div class="transferts-services" aria-label="Unités et transferts">
    <p v-if="erreur" class="ts-erreur ts-message" role="alert">{{ erreur }} <button type="button" class="btn btn-link btn-sm p-0" @click="charger()">Réessayer</button></p>
    <p v-if="info" class="ts-info ts-message" role="status">{{ info }}</p>

    <!-- Transferts décidés par le médecin : visibles sans clic, avec « Arrivé » (toutes unités). -->
    <div v-if="aAccueillir.length" class="ts-accueil" role="region" aria-label="Patients à accueillir">
      <p class="ts-accueil-titre"><span class="ts-point" aria-hidden="true"></span>{{ aAccueillir.length }} patient{{ aAccueillir.length > 1 ? 's' : '' }} à accueillir</p>
      <ul class="ts-liste">
        <li v-for="ligne in aAccueillir" :key="'r' + ligne.patient_id" class="ts-ligne">
          <div><strong>{{ nom(ligne) }}</strong> <span class="ts-fleche">→ {{ ligne.transfert_en_cours.vers }}</span><small class="d-block text-grey">
            En route depuis {{ depuis(ligne.transfert_en_cours.depuis) }}<template v-if="ligne.transfert_en_cours.par"> · demandé par {{ ligne.transfert_en_cours.par }}</template><template v-if="ligne.transfert_en_cours.motif"> · {{ ligne.transfert_en_cours.motif }}</template></small></div>
          <div class="ts-boutons">
            <button type="button" class="btn btn-success ts-bouton" :disabled="envoi" @click="confirmerArrivee(ligne)">Arrivé</button>
            <router-link :to="dossier(ligne)" class="btn btn-secondary-light-o ts-bouton">Dossier</router-link>
          </div>
        </li>
      </ul>
    </div>

    <!-- Unités : patients présents ; un clic affiche la liste de l'unité. -->
    <div v-if="services.length" class="ts-unites" role="group" aria-label="Unités">
      <span class="ts-unites-titre">Unités</span>
      <button v-for="service in services" :key="service.nom" type="button" :class="['ts-unite', { 'ts-unite--choisie': choisi === service.nom, 'ts-unite--alerte': service.enRoute.length }]"
        :aria-pressed="choisi === service.nom ? 'true' : 'false'" @click="choisi = choisi === service.nom ? '' : service.nom">
        {{ service.nom }} <strong>{{ service.presents.length }}</strong>
        <span v-if="service.enRoute.length" class="ts-unite-arrivees" :title="service.enRoute.length + ' à accueillir'">+{{ service.enRoute.length }}</span>
      </button>
    </div>
    <div v-if="serviceChoisi" class="ts-detail">
      <p v-if="!serviceChoisi.presents.length" class="text-grey mb-0">Aucun patient présent dans {{ serviceChoisi.nom }} pour le moment.</p>
      <ul v-else class="ts-liste">
        <li v-for="ligne in serviceChoisi.presents" :key="'p' + ligne.patient_id" class="ts-ligne">
          <div><strong>{{ nom(ligne) }}</strong><small class="d-block text-grey">
            Dans {{ serviceChoisi.nom }} depuis {{ depuis(ligne.position.depuis) }}<template v-if="ligne.sejour && ligne.sejour.chambre"> · lit : {{ libelleChambre(ligne.sejour.chambre) }}<template v-if="ligne.sejour.lit"> · {{ ligne.sejour.lit }}</template></template></small></div>
          <div class="ts-boutons"><router-link :to="dossier(ligne)" class="btn btn-secondary-light-o ts-bouton">Dossier</router-link></div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import api from '@/api/consultations/_parcours'
import { duree, secondesEntre, libelleChambre } from '@/components/parcours/parcours-utils'

// Poste de soins (04/10/2026) : unités de soins et transferts, intégrés dans la carte du poste de soins.
// Les transferts décidés par le médecin s'affichent sans clic avec « Arrivé » ; chaque unité montre ses présents.
export function regrouperParService(lignes) {
  const services = {}
  const service = nom => (services[nom] = services[nom] || { nom, enRoute: [], presents: [] })
  ;(Array.isArray(lignes) ? lignes : []).forEach(ligne => {
    if (!ligne || !ligne.position || ligne.position.code === 'sorti') return
    if (ligne.transfert_en_cours && ligne.transfert_en_cours.vers) { service(ligne.transfert_en_cours.vers).enRoute.push(ligne); return }
    if (ligne.sejour && ligne.sejour.service && ligne.position.code === 'service') service(ligne.sejour.service).presents.push(ligne)
  })
  // Unités avec des patients à accueillir d'abord, puis les plus remplies.
  return Object.keys(services).map(cle => services[cle])
    .sort((a, b) => (b.enRoute.length - a.enRoute.length) || (b.presents.length - a.presents.length) || a.nom.localeCompare(b.nom))
}

export default {
  name: 'transferts-services',
  data() {
    return { lignes: [], chargement: false, erreur: '', info: '', envoi: false, choisi: '', maintenant: new Date(), minuteur: null }
  },
  computed: {
    services() { return regrouperParService(this.lignes) },
    serviceChoisi() { return this.services.find(s => s.nom === this.choisi) || null },
    aAccueillir() { return this.services.reduce((liste, s) => liste.concat(s.enRoute), []) },
  },
  watch: {
    aAccueillir: { immediate: true, handler(liste) { this.$emit('compteur', liste.length) } },
  },
  mounted() {
    this.charger()
    this.minuteur = setInterval(() => { this.maintenant = new Date(); this.charger(true) }, 30000)
  },
  beforeDestroy() { clearInterval(this.minuteur) },
  methods: {
    libelleChambre,
    nom(ligne) { return [String(ligne.nom || '').toUpperCase(), ligne.prenoms].filter(Boolean).join(' ') },
    depuis(iso) { return duree(secondesEntre(iso, this.maintenant)) },
    dossier(ligne) { return { name: 'patientInfo', params: { id: ligne.patient_id, consultation: 'consulter' } } },
    async charger(silencieux) {
      if (!silencieux) this.chargement = true
      try {
        const reponse = await api.patientsPresents(false)
        this.lignes = reponse && Array.isArray(reponse.data) ? reponse.data : []
        this.erreur = ''
        // Une unité choisie qui n'a plus de patients se referme.
        if (this.choisi && !this.services.some(s => s.nom === this.choisi)) this.choisi = ''
      } catch (erreur) {
        if (!silencieux) this.erreur = 'Les unités et transferts n’ont pas pu être chargés.'
      } finally {
        this.chargement = false
        this.maintenant = new Date()
      }
    },
    async confirmerArrivee(ligne) {
      if (!ligne.transfert_en_cours) return
      this.envoi = true
      this.erreur = ''
      try {
        await api.confirmerArrivee(ligne.transfert_en_cours.id)
        this.info = 'Arrivée de ' + this.nom(ligne) + ' en ' + ligne.transfert_en_cours.vers + ' confirmée.'
        setTimeout(() => { this.info = '' }, 5000)
        await this.charger(true)
        this.$emit('arrivee')
      } catch (erreur) {
        const donnees = erreur && erreur.response ? erreur.response.data : null
        this.erreur = (donnees && donnees.message) || 'L’arrivée n’a pas pu être confirmée.'
      } finally {
        this.envoi = false
      }
    },
  },
}
</script>

<style scoped>
.transferts-services { margin: 4px 0 18px; color: var(--ui-text, #172033); }
.transferts-services .text-grey { color: var(--ui-text-muted, #526074) !important; }
.ts-message { margin: 0 0 10px; padding: 10px 14px; border-radius: 12px; }
.ts-info { background: #edf7f1; color: #195136; }
.ts-erreur { background: #fff0f0; color: #9b2626; }
.ts-erreur .btn-link { color: #214db0; text-decoration: underline; }
.ts-accueil { margin-bottom: 12px; padding: 10px 14px; border: 1px solid #f1b4b4; border-radius: 14px; background: #fff8f8; }
.ts-accueil-titre { display: flex; align-items: center; gap: 8px; margin: 0 0 4px; color: #9b2626; font-weight: 700; }
.ts-point { width: 9px; height: 9px; border-radius: 50%; background: #c62828; animation: ts-clignote 1.2s ease-in-out infinite; }
.ts-fleche { color: #9b2626; font-weight: 600; }
.ts-unites { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.ts-unites-titre { margin-right: 4px; color: var(--ui-text-muted, #526074); font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; }
.ts-unite { display: inline-flex; align-items: center; gap: 8px; min-height: 38px; padding: 6px 14px; border: 1px solid #c7d5f1; border-radius: 999px; background: #fff; color: var(--ui-text, #172033); font-weight: 600; font-size: 14px; }
.ts-unite strong { min-width: 22px; padding: 0 7px; border-radius: 999px; background: #edf3ff; color: var(--ui-text, #172033); text-align: center; }
.ts-unite:hover { background: #eaf0ff; }
.ts-unite--choisie { border-color: var(--ui-accent, #2457c5); background: #edf3ff; }
.ts-unite--alerte { border-color: #f1b4b4; }
.ts-unite-arrivees { padding: 0 8px; border-radius: 999px; background: #b42318; color: #fff; font-size: 12px; animation: ts-anneau 1.8s ease-out infinite; }
.ts-detail { margin-top: 10px; padding: 8px 14px; border: 1px solid #e0e7f3; border-radius: 12px; background: #fbfcff; }
.ts-liste { list-style: none; margin: 0; padding: 0; }
.ts-ligne { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 8px 0; border-bottom: 1px solid #edf0f6; }
.ts-ligne:last-child { border-bottom: 0; }
.ts-boutons { display: flex; flex: none; gap: 6px; }
.ts-bouton { min-height: 36px; padding: 7px 16px; border-radius: 999px; font-size: 14px; font-weight: 600; white-space: nowrap; }
.transferts-services button:focus-visible, .transferts-services a:focus-visible { outline: 3px solid #8caaf5; outline-offset: 2px; }
@keyframes ts-clignote { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }
@keyframes ts-anneau { 0% { box-shadow: 0 0 0 0 rgba(180, 35, 24, .45); } 100% { box-shadow: 0 0 0 7px rgba(180, 35, 24, 0); } }
@media (prefers-reduced-motion: reduce) { .ts-point, .ts-unite-arrivees { animation: none; } }
@media (max-width: 700px) { .ts-ligne { flex-direction: column; align-items: flex-start; } }
</style>
